import React, { useEffect, useState, useMemo } from 'react';
import { Grid, Typography, MenuItem, TextField, CircularProgress, Box, FormControl, Button } from '@mui/material';
import CustomModal from 'components/CustomModal';
import BankingService from 'services/banking.service';
import { useSnackbar } from 'notistack';

type Props = {
  open: boolean;
  accountType: string;
  onClose: () => void;
};

const bankingService = new BankingService();

const IncomeReportModal = ({ open, accountType, onClose }: Props) => {
  const { enqueueSnackbar } = useSnackbar();

  const [loading, setLoading] = useState(false);
  const [data, setData] = useState<{ year: number; quarters: number[] }[]>([]);

  const [year, setYear] = useState<number | null>(null);
  const [quarter, setQuarter] = useState<number | null>(null);

  const hasNoData = !loading && data.length === 0;

  useEffect(() => {
    if (!open) return;

    const fetchQuarters = async () => {
      setLoading(true);
      setYear(null);
      setQuarter(null);

      try {
        const response = await bankingService.getAccountIncomeQuarters();
        setData(response);
      } catch {
        setData([]);
      } finally {
        setLoading(false);
      }
    };

    fetchQuarters();
  }, [open]);

  const availableQuarters = useMemo(() => {
    if (year == null) return [];
    return data.find((d) => d.year === year)?.quarters ?? [];
  }, [data, year]);

  const inputStyle = {
    '& .MuiInputBase-root': {
      height: 37,
      borderRadius: '8px',
      textAlign: 'left',
    },
    '& input': {
      fontSize: '14px',
    },
  };

  const canExport = !loading && !hasNoData && year !== null && quarter !== null;

  const handleExport = async () => {
    if (!canExport) return;

    if (year == null || quarter == null) return;

    setLoading(true);

    try {
      await bankingService.exportIncomeReport({ year, quarter, accountType });

      enqueueSnackbar('O informe de rendimentos está sendo gerado. Você receberá uma notificação quando estiver pronto para download.', {
        variant: 'success',
      });

      onClose();
    } catch {
      enqueueSnackbar('Erro ao gerar o informe de rendimentos. Tente novamente mais tarde.', {
        variant: 'error',
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <CustomModal open={open} onClose={onClose} sx={{ p: '30px', minWidth: '492px' }}>
      <Grid container spacing={3}>
        <Grid item xs={12}>
          <Typography variant="h4" fontWeight={600} mb={1}>
            Baixar informe de rendimentos
          </Typography>
          <Typography variant="body1" fontWeight={500} color="#4E4D4D">
            Selecione o ano e trimestre para o qual deseja gerar o informe de rendimentos.
          </Typography>
        </Grid>

        {loading && (
          <Grid item xs={12}>
            <Box display="flex" justifyContent="center" py={3}>
              <CircularProgress size={32} />
            </Box>
          </Grid>
        )}

        {hasNoData && (
          <Grid item xs={12}>
            <Box textAlign="center" py={3} color="#666" fontSize="14px" fontWeight={500}>
              <Typography variant="body2">Nenhum período disponível para geração do informe.</Typography>
            </Box>
          </Grid>
        )}

        {!loading && !hasNoData && (
          <>
            <Grid item xs={6}>
              <FormControl fullWidth sx={{ minWidth: 200 }}>
                <Typography variant="body2" color="#666" mb={1} textAlign="left">
                  Ano
                </Typography>

                <TextField
                  select
                  value={year ?? ''}
                  onChange={(e) => {
                    setYear(Number(e.target.value));
                    setQuarter(null);
                  }}
                  sx={inputStyle}
                >
                  {data.map((item) => (
                    <MenuItem key={item.year} value={item.year}>
                      {item.year}
                    </MenuItem>
                  ))}
                </TextField>
              </FormControl>
            </Grid>

            <Grid item xs={6}>
              <FormControl fullWidth sx={{ minWidth: 200 }}>
                <Typography variant="body2" color="#666" mb={1} textAlign="left">
                  Trimestre
                </Typography>

                <TextField
                  select
                  value={quarter ?? ''}
                  onChange={(e) => setQuarter(Number(e.target.value))}
                  disabled={year == null}
                  sx={inputStyle}
                >
                  {year == null && <MenuItem value="">Selecione um ano primeiro</MenuItem>}

                  {year != null &&
                    availableQuarters.map((q) => (
                      <MenuItem key={q} value={q}>
                        {q}
                      </MenuItem>
                    ))}
                </TextField>
              </FormControl>
            </Grid>
          </>
        )}

        <Grid item xs={12}>
          <Box display="flex" justifyContent="flex-end" gap={2} mt={2}>
            <Button variant="outlined" color="inherit" onClick={onClose} disabled={loading}>
              Cancelar
            </Button>

            <Button variant="contained" onClick={handleExport} disabled={!canExport}>
              Exportar
            </Button>
          </Box>
        </Grid>
      </Grid>
    </CustomModal>
  );
};

export default IncomeReportModal;
